import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    IconButton, Menu, MenuItem, Dialog, DialogActions, 
    DialogContent, DialogContentText, DialogTitle, Button, Box, Typography
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { 
    getPricelistsCustomer, 
    deletePricelistCustomer,
    exportPricelistsCustomerToXML,
    updatePricelistCustomer 
} from '../Service/productServices';
import { getProducts } from '../Service/productServices';
import TopHeader from '../../common/Header/TopHeader';
import AddCustomerPriceDrawer from './AddCustomerPriceDrawer';
import CustomerPriceListFilter from './CustomerPriceListFilter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const CustomerPriceList = () => {
    const [pricelists, setPricelists] = useState([]);
    const [filteredPricelists, setFilteredPricelists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [selectedPricelist, setSelectedPricelist] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
    const [actionRow, setActionRow] = useState(null);
    const [productFilter, setProductFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [products, setProducts] = useState([]);
    const [editingPrice, setEditingPrice] = useState(null);
    const [tempPrice, setTempPrice] = useState('');

    const fetchPricelists = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {};
            if (productFilter) filters.productid = productFilter;
            if (activeFilter !== 'all') filters.active = activeFilter === 'active';
            
            const data = await getPricelistsCustomer(filters);
            const pricelistsArray = Array.isArray(data) ? data : data.data || [];
            setPricelists(pricelistsArray);
            
            // Apply search filter
            if (searchTerm) {
                const filtered = pricelistsArray.filter(pl => 
                    pl.productid?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    pl.productid?.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    pl.userid?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    pl.companyid?.name?.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setFilteredPricelists(filtered);
            } else {
                setFilteredPricelists(pricelistsArray);
            }
        } catch (err) {
            console.error('Error al obtener las tarifas', err);
            setPricelists([]);
            setFilteredPricelists([]);
        } finally {
            setLoading(false);
        }
    }, [productFilter, activeFilter, searchTerm]);

    const fetchProducts = useCallback(async () => {
        try {
            const data = await getProducts({});
            const productsArray = Array.isArray(data) ? data : [];
            setProducts(productsArray);
        } catch (err) {
            console.error('Error al obtener productos:', err);
            setProducts([]);
        }
    }, []);

    useEffect(() => {
        fetchPricelists();
        fetchProducts();
    }, [fetchPricelists, fetchProducts]);

    const handleEdit = (pricelist) => {
        setSelectedPricelist(pricelist);
        setEditDrawerOpen(true);
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('¿Estás seguro de eliminar esta tarifa?');
        if (confirmDelete) {
            try {
                await deletePricelistCustomer(id);
                fetchPricelists();
            } catch (err) {
                console.error('Error al eliminar tarifa:', err);
            }
        }
    };

    const handlePriceUpdate = async (pricelistId, newPrice) => {
        try {
            // Validar que el precio sea un número válido
            const priceValue = parseFloat(newPrice);
            if (isNaN(priceValue) || priceValue < 0) {
                alert('Please enter a valid price');
                return;
            }

            // Actualizar en el backend
            await updatePricelistCustomer(pricelistId, { price: priceValue });
            
            // Refrescar la lista
            fetchPricelists();
            
            // Limpiar el estado de edición
            setEditingPrice(null);
            setTempPrice('');
        } catch (err) {
            console.error('Error updating price:', err);
            alert('Error updating price');
        }
    };

    const handleBulkDelete = async () => {
        const ids = Object.keys(rowSelection).map(index => filteredPricelists[index]._id);
        for (let id of ids) {
            await deletePricelistCustomer(id);
        }
        setDeleteConfirmOpen(false);
        setRowSelection({});
        fetchPricelists();
    };

    const handlePricelistAdded = () => {
        fetchPricelists();
        setAddDrawerOpen(false);
        setEditDrawerOpen(false);
        setSelectedPricelist(null);
    };

    const handleCloseDrawer = () => {
        setAddDrawerOpen(false);
        setEditDrawerOpen(false);
        setSelectedPricelist(null);
    };

    const exportCSV = () => {
        const headers = ['Product Code', 'Product Name', 'Customer/Company', 'Min Qty', 'Max Qty', 'Price', 'Priority', 'Active', 'Start Date', 'End Date'];
        const rows = filteredPricelists.map(pl => {
            // Convertir precio correctamente
            let price = '0';
            if (pl.price) {
                if (pl.price.$numberDecimal) {
                    price = pl.price.$numberDecimal;
                } else {
                    price = pl.price.toString();
                }
            }
            
            return [
                pl.productid?.code || '',
                pl.productid?.name || '',
                pl.userid?.name || pl.companyid?.name || 'N/A',
                pl.minQuantity || 1,
                pl.maxQuantity || 'N/A',
                parseFloat(price).toFixed(2),
                pl.priority || 0,
                pl.active ? 'Yes' : 'No',
                pl.startDate ? new Date(pl.startDate).toLocaleDateString() : 'N/A',
                pl.endDate ? new Date(pl.endDate).toLocaleDateString() : 'N/A'
            ];
        });
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'customer_pricelists.csv';
        a.click();
    };

    const exportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredPricelists.map(pl => {
            // Convertir precio correctamente
            let price = '0';
            if (pl.price) {
                if (pl.price.$numberDecimal) {
                    price = pl.price.$numberDecimal;
                } else {
                    price = pl.price.toString();
                }
            }
            
            return {
                'Product Code': pl.productid?.code || '',
                'Product Name': pl.productid?.name || '',
                'Customer/Company': pl.userid?.name || pl.companyid?.name || 'N/A',
                'Min Quantity': pl.minQuantity || 1,
                'Max Quantity': pl.maxQuantity || 'N/A',
                'Price': parseFloat(price).toFixed(2),
                'Currency': pl.currency || '',
                'Priority': pl.priority || 0,
                'Active': pl.active ? 'Yes' : 'No',
                'Start Date': pl.startDate ? new Date(pl.startDate).toLocaleDateString() : 'N/A',
                'End Date': pl.endDate ? new Date(pl.endDate).toLocaleDateString() : 'N/A'
            };
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Customer Pricelists');
        XLSX.writeFile(wb, 'customer_pricelists.xlsx');
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        const headers = [['Product', 'Customer/Company', 'Min Qty', 'Price', 'Priority', 'Active']];
        const data = filteredPricelists.map(pl => {
            // Convertir precio correctamente
            let price = '0';
            if (pl.price) {
                if (pl.price.$numberDecimal) {
                    price = pl.price.$numberDecimal;
                } else {
                    price = pl.price.toString();
                }
            }
            
            return [
                pl.productid?.name || 'N/A',
                pl.userid?.name || pl.companyid?.name || 'N/A',
                pl.minQuantity || 1,
                `${parseFloat(price).toFixed(2)} ${pl.currency || '€'}`,
                pl.priority || 0,
                pl.active ? 'Yes' : 'No'
            ];
        });
        doc.autoTable({ head: headers, body: data });
        doc.save('customer_pricelists.pdf');
    };

    const handleRemoveFilter = (filterKey) => {
        switch(filterKey) {
            case 'product':
                setProductFilter('');
                break;
            case 'status':
                setActiveFilter('all');
                break;
            case 'search':
                setSearchTerm('');
                break;
            default:
                break;
        }
    };

    const handleClearAllFilters = () => {
        setProductFilter('');
        setActiveFilter('all');
        setSearchTerm('');
        setRowSelection({});
    };

    const handleClearSelection = () => {
        setRowSelection({});
    };

    const activeFiltersArray = useMemo(() => {
        const filters = [];

        if (productFilter) {
            const product = products.find(p => p._id === productFilter);
            filters.push({
                label: 'Product',
                value: product?.name || productFilter,
                key: 'product'
            });
        }

        if (activeFilter !== 'all') {
            filters.push({
                label: 'Status',
                value: activeFilter === 'active' ? 'Active' : 'Inactive',
                key: 'status'
            });
        }

        if (searchTerm) {
            filters.push({
                label: 'Search',
                value: searchTerm,
                key: 'search'
            });
        }

        const selectedCount = Object.keys(rowSelection).length;
        if (selectedCount > 0) {
            filters.push({
                label: 'Selected',
                value: selectedCount,
                key: 'selected'
            });
        }

        return filters;
    }, [productFilter, activeFilter, searchTerm, rowSelection, products]);

    const totalPricelists = Array.isArray(pricelists) ? pricelists.length : 0;
    const activePricelists = Array.isArray(pricelists) ? pricelists.filter(p => p.active).length : 0;

    const columns = useMemo(() => [
        {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ row }) => {
                const [anchorEl, setAnchorEl] = useState(null);
                const open = Boolean(anchorEl);

                const handleClick = (event) => {
                    setAnchorEl(event.currentTarget);
                };

                const handleClose = () => {
                    setAnchorEl(null);
                };

                const handleMenuAction = (action) => {
                    handleClose();
                    action();
                };

                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        <IconButton onClick={handleClick}>
                            <p>...</p>
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                            <MenuItem onClick={() => handleMenuAction(() => {
                                setActionRow(row.original);
                                setDetailsOpen(true);
                            })}>
                                View Details
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuAction(() => handleDelete(row.original._id))}>
                                Delete
                            </MenuItem>
                        </Menu>
                    </div>
                );
            },
            size: 50,
        },
        {
            accessorKey: "productid.name",
            header: "Product",
            Cell: ({ row }) => {
                const { productid } = row.original;
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        {productid?.imageUrl ? (
                            <img
                                src={productid.imageUrl}
                                alt={productid.name}
                                className="rounded-circle me-2"
                                style={{ width: 30, height: 30, objectFit: "cover" }}
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.style.display = "none";
                                }}
                            />
                        ) : (
                            <div
                                className="rounded-circle me-2"
                                style={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: "#ccc",
                                }}
                            />
                        )}
                        <div>
                            <div className="text-truncate fw-bold">{productid?.name || 'N/A'}</div>
                            <small className="text-muted">{productid?.code || ''}</small>
                        </div>
                    </div>
                );
            },
        },
        { 
            accessorKey: 'customer', 
            header: 'Customer/Company',
            Cell: ({ row }) => {
                const { userid, companyid } = row.original;
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        {userid ? (
                            <div>
                                <div className="fw-bold">{userid.name}</div>
                                <small className="text-muted">{userid.email}</small>
                            </div>
                        ) : companyid ? (
                            <div>
                                <div className="fw-bold">{companyid.name}</div>
                                <small className="text-muted">Company</small>
                            </div>
                        ) : (
                            <span className="text-muted">N/A</span>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'minQuantity',
            header: 'Min Qty',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    {row.original.minQuantity || 1}
                </div>
            )
        },
        {
            accessorKey: 'maxQuantity',
            header: 'Max Qty',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    {row.original.maxQuantity || 'N/A'}
                </div>
            )
        },
        {
            accessorKey: 'price',
            header: 'Price',
            Cell: ({ row }) => {
                const isEditing = editingPrice === row.original._id;
                
                // Convertir Decimal128 a número
                let price = '0';
                if (row.original.price) {
                    if (row.original.price.$numberDecimal) {
                        price = row.original.price.$numberDecimal;
                    } else if (typeof row.original.price === 'object' && row.original.price.toString) {
                        price = row.original.price.toString();
                    } else {
                        price = row.original.price.toString();
                    }
                }
                
                const currency = row.original.currency || '€';
                
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        {isEditing ? (
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="form-control form-control-sm"
                                    style={{ width: '100px' }}
                                    value={tempPrice}
                                    onChange={(e) => setTempPrice(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handlePriceUpdate(row.original._id, tempPrice);
                                        } else if (e.key === 'Escape') {
                                            setEditingPrice(null);
                                            setTempPrice('');
                                        }
                                    }}
                                    onBlur={() => {
                                        handlePriceUpdate(row.original._id, tempPrice);
                                    }}
                                    autoFocus
                                />
                                <span className="text-muted">{currency}</span>
                            </div>
                        ) : (
                            <span 
                                className="fw-bold text-success"
                                style={{ cursor: 'pointer' }}
                                onDoubleClick={() => {
                                    setEditingPrice(row.original._id);
                                    setTempPrice(price);
                                }}
                                title="Double click to edit"
                            >
                                {parseFloat(price).toFixed(2)} {currency}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'priority',
            header: 'Priority',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    <span className={`badge ${row.original.priority > 5 ? 'bg-danger' : row.original.priority > 2 ? 'bg-warning' : 'bg-secondary'}`}>
                        {row.original.priority || 0}
                    </span>
                </div>
            )
        },
        {
            accessorKey: 'dateRange',
            header: 'Date Range',
            Cell: ({ row }) => {
                const { startDate, endDate } = row.original;
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        <div className="small">
                            {startDate ? (
                                <div>{new Date(startDate).toLocaleDateString()}</div>
                            ) : (
                                <div className="text-muted">No start</div>
                            )}
                            {endDate ? (
                                <div>{new Date(endDate).toLocaleDateString()}</div>
                            ) : (
                                <div className="text-muted">No end</div>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            accessorKey: 'active',
            header: 'Status',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    <span className={`badge ${row.original.active ? 'bg-success' : 'bg-danger'}`}>
                        {row.original.active ? 'Active' : 'Inactive'}
                    </span>
                </div>
            )
        }
    ], [editingPrice, tempPrice]);

    return (
        <main id="app">
            <TopHeader />

            <div className="content_wrapper">
                <section className="doctor-management-wrapper bg-white pb-5">
                    <div className="container-fluid px-lg-5">

                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item"><Link to="/products">Products</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Customer Pricelists</li>
                            </ol>
                        </nav>

                        <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
                            <div>
                                <h2 className="mb-1">Customer Pricelists</h2>
                                <p className="text-muted mb-0">
                                    Manage custom pricing for specific customers and companies
                                </p>
                            </div>
                            <div className="d-flex gap-3">
                                <div className="text-center">
                                    <h4 className="mb-0 text-primary">{totalPricelists}</h4>
                                    <small className="text-muted">Total</small>
                                </div>
                                <div className="text-center">
                                    <h4 className="mb-0 text-success">{activePricelists}</h4>
                                    <small className="text-muted">Active</small>
                                </div>
                            </div>
                        </div>

                        <div className="filter-container py-3">
                            <div className="row align-items-center">
                                <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
                                    <div className="position-relative">
                                        <div className="input-group">
                                            <button type="submit" className="p-0 bg-transparent border-0">
                                                <span className="d-block material-symbols-outlined icon-lg">search</span>
                                            </button>
                                            <input 
                                                type="text"
                                                className="form-control ps-2 bg-transparent border-0 shadow-none"
                                                placeholder="Search by product, customer..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-2">
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn btn btn-gray dropdown-toggle d-flex align-items-center gap-1"
                                            type="button"
                                            onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                                        >
                                            <span className="d-block">Action</span>
                                        </button>
                                        <Menu
                                            anchorEl={actionsAnchorEl}
                                            open={Boolean(actionsAnchorEl)}
                                            onClose={() => setActionsAnchorEl(null)}
                                        >
                                            <MenuItem onClick={() => {
                                                exportCSV();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export CSV
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                exportExcel();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export Excel
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                exportPDF();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export PDF
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                exportPricelistsCustomerToXML();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export XML
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                setDeleteConfirmOpen(true);
                                                setActionsAnchorEl(null);
                                            }}>
                                                Delete Selected
                                            </MenuItem>
                                        </Menu>
                                    </div>

                                    <CustomerPriceListFilter
                                        productFilter={productFilter}
                                        setProductFilter={setProductFilter}
                                        activeFilter={activeFilter}
                                        setActiveFilter={setActiveFilter}
                                        products={products}
                                    />
                                    
                                    <button 
                                        className="btn btn-primary d-flex align-items-center gap-1" 
                                        onClick={() => setAddDrawerOpen(true)}
                                    >
                                        <span className="d-block material-symbols-outlined icon-md">add</span>
                                        <span className="d-block">Add New</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {activeFiltersArray.length > 0 && (
                            <div className="container-fluid mb-3 mt-3 border rounded p-3 bg-light">
                                <div className="row align-items-center">
                                    <div className="col-auto">
                                        <span className="text-muted me-2 fw-bold">Active Filters:</span>
                                        {activeFiltersArray.map((filter, index) => (
                                            <span 
                                                key={index} 
                                                className="badge bg-white text-dark border me-2 mb-2 d-inline-flex align-items-center"
                                                style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
                                            >
                                                <strong>{filter.label}:</strong> 
                                                <span className="ms-1">{filter.value}</span>
                                                <button 
                                                    className="btn btn-sm btn-link p-0 ms-2 text-danger"
                                                    onClick={() => {
                                                        if (filter.key === 'selected') {
                                                            handleClearSelection();
                                                        } else {
                                                            handleRemoveFilter(filter.key);
                                                        }
                                                    }}
                                                    style={{ textDecoration: 'none', fontSize: '1rem' }}
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="col-auto ms-auto">
                                        <button 
                                            className="btn btn-link text-primary fw-bold"
                                            onClick={handleClearAllFilters}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="table-wrapper">
                            <MaterialReactTable
                                columns={columns}
                                data={filteredPricelists}
                                state={{ isLoading: loading, rowSelection }}
                                onRowSelectionChange={setRowSelection}
                                enableRowSelection={true}
                                enableColumnFilters
                                enableColumnOrdering
                                enableSorting
                                enablePagination
                            />
                        </div>
                    </div>
                </section>
            </div>

            {/* Add Pricelist Drawer */}
            <AddCustomerPriceDrawer
                open={addDrawerOpen}
                onClose={handleCloseDrawer}
                selectedPricelist={null}
                onPricelistAdded={handlePricelistAdded}
                products={products}
            />

            {/* Edit Pricelist Drawer */}
            <AddCustomerPriceDrawer
                open={editDrawerOpen}
                onClose={handleCloseDrawer}
                selectedPricelist={selectedPricelist}
                onPricelistAdded={handlePricelistAdded}
                products={products}
            />

            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the selected pricelists?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleBulkDelete} color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Pricelist Details</DialogTitle>
                <DialogContent dividers>
                    {actionRow && (
                        <Box>
                            <Typography variant="h6" className="mb-3">
                                {actionRow.productid?.name || 'N/A'}
                            </Typography>
                            <div className="row">
                                <div className="col-md-6">
                                    <Typography><strong>Product Code:</strong> {actionRow.productid?.code || 'N/A'}</Typography>
                                    <Typography><strong>Customer:</strong> {actionRow.userid?.name || 'N/A'}</Typography>
                                    <Typography><strong>Company:</strong> {actionRow.companyid?.name || 'N/A'}</Typography>
                                    <Typography><strong>Price:</strong> {actionRow.price?.toString() || '0'} {actionRow.currency || '€'}</Typography>
                                    <Typography><strong>Min Quantity:</strong> {actionRow.minQuantity || 1}</Typography>
                                    <Typography><strong>Max Quantity:</strong> {actionRow.maxQuantity || 'N/A'}</Typography>
                                </div>
                                <div className="col-md-6">
                                    <Typography><strong>Priority:</strong> {actionRow.priority || 0}</Typography>
                                    <Typography><strong>Status:</strong> {actionRow.active ? 'Active' : 'Inactive'}</Typography>
                                    <Typography><strong>Start Date:</strong> {actionRow.startDate ? new Date(actionRow.startDate).toLocaleDateString() : 'N/A'}</Typography>
                                    <Typography><strong>End Date:</strong> {actionRow.endDate ? new Date(actionRow.endDate).toLocaleDateString() : 'N/A'}</Typography>
                                    {actionRow.couponCodes && actionRow.couponCodes.length > 0 && (
                                        <Typography><strong>Coupon Codes:</strong> {actionRow.couponCodes.join(', ')}</Typography>
                                    )}
                                </div>
                            </div>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </main>
    );
};

export default CustomerPriceList;